"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { LogIn, Eye, EyeOff, AlertCircle, ShieldCheck, KeyRound, Loader2 } from "lucide-react";
import { getSupabaseBrowser } from "@/lib/supabase/browser";
import { BrandLogo } from "@/components/BrandLogo";
import { markSessionPolicy } from "@/lib/auth/session-policy";
import { toErrorMessage, isTransientServerError } from "@/lib/errors";

async function getVerifiedTotpFactorId(
  supabase: ReturnType<typeof getSupabaseBrowser>,
): Promise<string | null> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return null;
  const verified = (data.totp ?? []).find((f) => f.status === "verified");
  return verified?.id ?? null;
}

async function resolveRedirect(explicit: string | null): Promise<string> {
  if (explicit) return explicit;
  try {
    const res = await fetch("/api/auth/whoami", { cache: "no-store" });
    if (!res.ok) return "/dashboard";
    const data = (await res.json()) as { redirect_target?: string };
    return data.redirect_target || "/dashboard";
  } catch {
    return "/dashboard";
  }
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Wenn explizit ein redirect-Param gesetzt ist (z. B. weil die Middleware
  // jemanden von /admin/* hierher umgeleitet hat), gewinnt der.
  // Ohne expliziten redirect schauen wir nach erfolgreichem Login per
  // /api/auth/whoami nach, wo der User hingehört (Admin → /admin,
  // Kunde → /dashboard).
  const explicitRedirect = searchParams.get("redirect");
  const errorParam = searchParams.get("error");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Angemeldet-bleiben-Default: AUS, damit der Standardfall (z. B.
  // geteiltes Gerät, Internet-Café) sicher ist. Wer es will, klickt es an.
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(errorParam);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  // 2FA-State (zweiter Login-Schritt). Wird nur aktiv, wenn der User
  // ein verifiziertes TOTP-Factor hat. Sonst überspringen wir den Schritt
  // komplett – Nicht-2FA-Nutzer sollen nichts davon merken.
  type MfaState =
    | { kind: "none" }
    | {
        kind: "totp";
        factorId: string;
        code: string;
        useRecovery: boolean;
        recoveryCode: string;
        busy: boolean;
        info: string | null;
      };
  const [mfa, setMfa] = useState<MfaState>({ kind: "none" });

  useEffect(() => {
    let active = true;
    const supabase = getSupabaseBrowser();
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!active) return;
      if (!data.user) {
        setCheckingSession(false);
        return;
      }
      // Eingeloggt – aber haben wir schon 2FA bestanden, falls nötig?
      const { data: aalData } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (
        aalData &&
        aalData.nextLevel === "aal2" &&
        aalData.currentLevel === "aal1"
      ) {
        // 2FA-Schritt fehlt noch
        const factorId = await getVerifiedTotpFactorId(supabase);
        if (factorId) {
          setMfa({
            kind: "totp",
            factorId,
            code: "",
            useRecovery: false,
            recoveryCode: "",
            busy: false,
            info: null,
          });
          setCheckingSession(false);
          return;
        }
      }
      const target = await resolveRedirect(explicitRedirect);
      router.replace(target);
    })();
    return () => {
      active = false;
    };
  }, [router, explicitRedirect]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabaseBrowser();
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (authError) {
        // Defensive: authError.message kann bei Server-Problemen leer/undefined
        // sein (z. B. 503 vom Auth-Gateway) → nie ungeprüft .toLowerCase().
        const lower = (authError.message ?? "").toLowerCase();
        if (isTransientServerError(authError)) {
          setError(
            "Der Anmelde-Dienst ist gerade kurz nicht erreichbar. Bitte versuch es in einem Moment noch einmal.",
          );
        } else if (
          lower.includes("email not confirmed") ||
          lower.includes("not_confirmed")
        ) {
          // E-Mail noch nicht bestätigt → klare UX inkl. Resend-Button.
          setError(
            "Ihre E-Mail-Adresse ist noch nicht bestätigt. Bitte klicken Sie den Link aus der Bestätigungs-Mail. Keine Mail erhalten? Unten neu senden.",
          );
          setShowResendConfirm(true);
        } else {
          setError(
            lower.includes("invalid login credentials")
              ? "E-Mail oder Passwort ist falsch."
              : toErrorMessage(authError, "Anmeldung fehlgeschlagen. Bitte versuch es erneut."),
          );
        }
        return;
      }

      // Session ist jetzt gesetzt. Bevor wir weiterleiten: prüfen, ob
      // dieser User 2FA aktiviert hat. Wenn ja → 2FA-Schritt anzeigen,
      // NICHT redirecten. Sonst direkt zu Dashboard/Admin.
      const aalRes = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (
        aalRes.data?.nextLevel === "aal2" &&
        aalRes.data?.currentLevel === "aal1"
      ) {
        const factorId = await getVerifiedTotpFactorId(supabase);
        if (factorId) {
          setMfa({
            kind: "totp",
            factorId,
            code: "",
            useRecovery: false,
            recoveryCode: "",
            busy: false,
            info: null,
          });
          return;
        }
      }
      // Session-Policy markieren bevor wir weiterleiten – Guard liest die
      // Flags beim nächsten Render.
      markSessionPolicy(rememberMe);
      const target = await resolveRedirect(explicitRedirect);
      router.push(target);
      router.refresh();
    } catch (err) {
      setError(
        isTransientServerError(err)
          ? "Der Anmelde-Dienst ist gerade kurz nicht erreichbar. Bitte versuch es in einem Moment noch einmal."
          : toErrorMessage(err, "Anmeldung fehlgeschlagen. Bitte versuch es erneut."),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfa.kind !== "totp") return;
    setMfa({ ...mfa, busy: true, info: null });
    setError(null);
    const supabase = getSupabaseBrowser();

    try {
      if (mfa.useRecovery) {
        // Recovery-Code-Pfad: RPC verbraucht Code + entfernt MFA-Faktor.
        // Danach ist Session wieder aal1 und User kann normal weiter.
        const { data: ok, error: rErr } = await (supabase.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: boolean | null; error: { message: string } | null }>)(
          "consume_my_recovery_code",
          { plain_code: mfa.recoveryCode.trim() },
        );
        if (rErr) throw new Error(rErr.message);
        if (ok !== true) {
          setMfa({
            ...mfa,
            busy: false,
            info: "Recovery-Code ungültig oder schon verbraucht.",
          });
          return;
        }
        // 2FA wurde zurückgesetzt – Session refreshen, dann redirecten.
        // (Die Anfangs-Session ist aal1, jetzt gibt's keine Faktoren mehr,
        // also reicht aal1 ab sofort.)
        await supabase.auth.refreshSession();
        markSessionPolicy(rememberMe);
      } else {
        // TOTP-Pfad: Challenge starten und mit Code verifizieren.
        const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
          factorId: mfa.factorId,
        });
        if (chErr || !ch) throw new Error(chErr?.message ?? "Challenge fehlgeschlagen");
        const { error: vErr } = await supabase.auth.mfa.verify({
          factorId: mfa.factorId,
          challengeId: ch.id,
          code: mfa.code.trim(),
        });
        if (vErr) {
          setMfa({ ...mfa, busy: false, info: "Code falsch oder abgelaufen." });
          return;
        }
        markSessionPolicy(rememberMe);
      }
      const target = await resolveRedirect(explicitRedirect);
      router.push(target);
      router.refresh();
    } catch (err) {
      setMfa({ ...mfa, busy: false, info: (err as Error).message });
    }
  };

  if (checkingSession) {
    return (
      <div
        className="mt-8 flex items-center justify-center rounded-xl border p-12"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
      >
        <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
          Bitte warten …
        </p>
      </div>
    );
  }

  // ───── 2. Schritt: 2FA-Verifizierung ─────
  if (mfa.kind === "totp") {
    return (
      <form
        onSubmit={submitMfa}
        className="mt-8 space-y-4 rounded-xl border p-6"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
            style={{
              backgroundColor: "var(--fi-brand-bg)",
              color: "var(--fi-brand-fg)",
            }}
          >
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>
              Zwei-Faktor-Authentifizierung
            </h2>
            <p className="mt-0.5 text-sm" style={{ color: "var(--text-tertiary)" }}>
              {mfa.useRecovery
                ? "Geben Sie einen Ihrer Recovery-Codes ein. Er funktioniert genau einmal und deaktiviert anschließend Ihre 2FA."
                : "Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein."}
            </p>
          </div>
        </div>

        {!mfa.useRecovery ? (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoFocus
            value={mfa.code}
            onChange={(e) =>
              setMfa({
                ...mfa,
                code: e.target.value.replace(/\D/g, "").slice(0, 6),
              })
            }
            placeholder="123456"
            className="w-full rounded-lg border px-4 py-3 text-center font-mono text-xl tracking-widest outline-none focus:border-brand-500"
            style={{
              backgroundColor: "var(--bg)",
              borderColor: "var(--border)",
              color: "var(--text)",
            }}
            disabled={mfa.busy}
          />
        ) : (
          <input
            type="text"
            autoFocus
            value={mfa.recoveryCode}
            onChange={(e) =>
              setMfa({ ...mfa, recoveryCode: e.target.value.toUpperCase() })
            }
            placeholder="XXXX-XXXX-XXXX"
            className="w-full rounded-lg border px-4 py-3 text-center font-mono text-base tracking-widest outline-none focus:border-brand-500"
            style={{
              backgroundColor: "var(--bg)",
              borderColor: "var(--border)",
              color: "var(--text)",
            }}
            disabled={mfa.busy}
          />
        )}

        {mfa.info && (
          <div
            className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm"
            style={{
              backgroundColor: "var(--fi-error-bg)",
              borderColor: "var(--border)",
              color: "var(--fi-error-fg)",
            }}
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{mfa.info}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={
            mfa.busy ||
            (!mfa.useRecovery && mfa.code.length !== 6) ||
            (mfa.useRecovery && mfa.recoveryCode.length < 10)
          }
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {mfa.busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ShieldCheck className="h-4 w-4" />
          )}
          {mfa.useRecovery ? "Recovery-Code einlösen" : "Anmelden"}
        </button>

        <button
          type="button"
          onClick={() =>
            setMfa({
              ...mfa,
              useRecovery: !mfa.useRecovery,
              code: "",
              recoveryCode: "",
              info: null,
            })
          }
          className="flex w-full items-center justify-center gap-1.5 text-xs hover:underline"
          style={{ color: "var(--text-tertiary)" }}
        >
          <KeyRound className="h-3 w-3" />
          {mfa.useRecovery
            ? "Doch normalen 6-stelligen Code eingeben"
            : "Handy verloren? Recovery-Code verwenden"}
        </button>

        <button
          type="button"
          onClick={async () => {
            await getSupabaseBrowser().auth.signOut();
            setMfa({ kind: "none" });
            setEmail("");
            setPassword("");
          }}
          className="flex w-full items-center justify-center text-xs hover:underline"
          style={{ color: "var(--text-quaternary)" }}
        >
          Mit anderem Account anmelden
        </button>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-8 space-y-4 rounded-xl border p-6"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
    >
      <div>
        <label
          htmlFor="login-email"
          className="mb-1.5 block text-sm font-medium"
          style={{ color: "var(--text)" }}
        >
          E-Mail
        </label>
        <input
          type="email"
          id="login-email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
          style={{
            backgroundColor: "var(--bg)",
            borderColor: "var(--border)",
            color: "var(--text)",
          }}
        />
      </div>

      <div>
        <label
          htmlFor="login-password"
          className="mb-1.5 block text-sm font-medium"
          style={{ color: "var(--text)" }}
        >
          Passwort
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            id="login-password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className="w-full rounded-lg border px-4 py-2.5 pr-10 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
            style={{
              backgroundColor: "var(--bg)",
              borderColor: "var(--border)",
              color: "var(--text)",
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" style={{ color: "var(--text-secondary)" }} />
            ) : (
              <Eye className="h-4 w-4" style={{ color: "var(--text-secondary)" }} />
            )}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <label
          className="flex cursor-pointer items-center gap-2 text-xs select-none"
          style={{ color: "var(--text-secondary)" }}
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={loading}
            className="h-3.5 w-3.5 accent-brand-500"
          />
          Angemeldet bleiben
        </label>
        <Link
          href="/login/passwort-vergessen"
          className="text-xs text-brand-500 hover:underline"
        >
          Passwort vergessen?
        </Link>
      </div>
      <p
        className="-mt-2 text-[10.5px] leading-snug"
        style={{ color: "var(--text-quaternary)" }}
      >
        Ohne Häkchen werden Sie nach 8 Stunden ohne Aktivität automatisch
        abgemeldet. Mit Häkchen bleiben Sie auf diesem Gerät angemeldet
        (bis zu 30 Tage Inaktivität). Auf gemeinsam genutzten Geräten
        besser aus lassen.
      </p>

      {error && (
        <div
          className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm"
          style={{
            backgroundColor: "var(--fi-error-bg)",
            borderColor: "var(--border)",
            color: "var(--fi-error-fg)",
          }}
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {showResendConfirm && (
        <div className="-mt-2">
          {resendDone ? (
            <p
              className="text-xs"
              style={{ color: "var(--text-success-primary)" }}
            >
              ✓ Bestätigungs-Mail neu gesendet. Bitte sehen Sie in Ihr Postfach.
            </p>
          ) : (
            <button
              type="button"
              disabled={resending || !email}
              onClick={async () => {
                if (!email) return;
                setResending(true);
                try {
                  const supabase = getSupabaseBrowser();
                  const { error: rErr } = await supabase.auth.resend({
                    type: "signup",
                    email: email.trim().toLowerCase(),
                    options: {
                      emailRedirectTo: `${window.location.origin}/auth/callback`,
                    },
                  });
                  if (rErr) throw rErr;
                  setResendDone(true);
                } catch (e) {
                  setError(
                    `Erneutes Senden fehlgeschlagen: ${(e as Error).message}`,
                  );
                } finally {
                  setResending(false);
                }
              }}
              className="text-xs font-semibold text-brand-500 hover:underline disabled:opacity-50"
            >
              {resending ? "Sende …" : "Bestätigungs-Mail erneut senden"}
            </button>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !email || !password}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
      >
        <LogIn className="h-4 w-4" />
        {loading ? "Anmelden …" : "Anmelden"}
      </button>
    </form>
  );
}

export default function LoginPage() {
  return (
    <section className="flex min-h-[70vh] items-center py-16">
      <div className="mx-auto w-full max-w-md px-4">
        <div className="text-center">
          <div className="flex justify-center">
            <BrandLogo priority />
          </div>
          <h1 className="mt-6 text-2xl font-bold" style={{ color: "var(--text)" }}>
            Willkommen zurück
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            Melden Sie sich in Ihrem Konto an.
          </p>
        </div>

        <Suspense fallback={null}>
          <LoginForm />
        </Suspense>

        <p className="mt-4 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
          Noch kein Konto?{" "}
          <Link href="/registrierung" className="font-medium text-brand-500 hover:underline">
            Jetzt registrieren
          </Link>
        </p>
      </div>
    </section>
  );
}
